import { isNull } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { PromoCodeForm } from './PromoCodeForm.tsx';
import { PricingBreakdown as TPricingBreakdown } from './types.ts';

type Props = {
  pricingBreakdown: TPricingBreakdown;
  onPromoCodeFormSuccess: (promoCode: string) => void;
};

export function PricingBreakdownSummary({ pricingBreakdown, onPromoCodeFormSuccess }: Props) {
  const { subtotalExcludingTax, discount, tax, total, totalDueToday } = pricingBreakdown;

  const promoCodeDescription = discount
    ? `${discount.promotionCode} - ${discount.couponName} (${discount.percent}% off)`
    : '';

  return (
    <Flex flexDirection="column" gap="0.2rem">
      <Row>
        <Typography variant="h4">Subtotal</Typography>
        <Typography variant="h4">{subtotalExcludingTax.toString()}</Typography>
      </Row>
      <Row>
        {isNull(discount) ? (
          <PromoCodeFormToggle onSuccess={onPromoCodeFormSuccess} />
        ) : (
          <>
            <Typography variant="h4" color="textTertiary">
              Promo code {promoCodeDescription}
            </Typography>
            <Typography variant="h4" color="textTertiary">
              {discount ? `-${discount.amount.toString()}` : '--'}
            </Typography>
          </>
        )}
      </Row>
      <Row>
        <Typography variant="h4" color="textTertiary">
          Sales tax
        </Typography>
        <Typography variant="h4" color="textTertiary">
          {tax?.toString() ?? '--'}
        </Typography>
      </Row>
      <Block height="1px" backgroundColor="textTertiary" />
      {isNull(totalDueToday) ? (
        <Row>
          <Typography variant="h3">Total</Typography>
          <Typography variant="h3">{total.toString()}</Typography>
        </Row>
      ) : (
        <>
          <Row>
            <Typography variant="h4" color="textTertiary">
              Total
            </Typography>
            <Typography variant="h4" color="textTertiary">
              {total.toString()}
            </Typography>
          </Row>
          <Row>
            <Typography variant="h3">Total due today</Typography>
            <Typography variant="h3">{totalDueToday.toString()}</Typography>
          </Row>
        </>
      )}
    </Flex>
  );
}

function Row({ children }: React.PropsWithChildren) {
  return <Flex justifyContent="space-between">{children}</Flex>;
}

function PromoCodeFormToggle({ onSuccess }: { onSuccess: Props['onPromoCodeFormSuccess'] }) {
  const [showForm, setShowForm] = useState(false);

  return showForm ? (
    <PromoCodeForm onSuccess={onSuccess} />
  ) : (
    <Button
      variant="inline"
      text="Add promo code"
      onClick={() => setShowForm(true)}
      __dangerDoNotUseSx={{
        padding: 0,
        height: 'unset',
      }}
    />
  );
}
